import { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '../ui/Button';
import { SYSTEM_COLLECTORS } from '../../lib/systemMetrics';
import { requestMappingDiscovery, fetchMappingDiscovery, type SystemDescribe } from '../../api/dataApi';

// Live "Discover" for a "system" (host-metrics) mapping: asks a connected
// zagentc daemon what it actually sees on its host (sysmetrics_describe's
// dry pass - see zagentc/src/sysmetrics.h), then lets the operator turn
// collectors/mounts/interfaces on or off from that real list instead of
// guessing field names into the raw spec JSON. Edits flow back through
// `onChange` as a patch - this component owns no save/publish state of its
// own, same as every other field on MappingManageScreen's Spec section.
//
// Only meaningful once a mapping is already saved (it needs a real
// nickname/channel for an agent to answer into) - the caller only renders
// this for an existing "system" mapping, never during creation.

export interface SystemSpecPatch {
  collectors?: string[];
  dfMounts?: string[];
  netInterfaces?: string[];
  netExclude?: string[];
}

interface Props {
  nickname: string;
  collectors: string[];
  dfMounts: string[];
  netInterfaces: string[];
  netExclude: string[];
  onChange: (patch: SystemSpecPatch) => void;
}

type Status = 'idle' | 'waiting' | 'timeout' | 'error';

const POLL_MS = 1500;
const TIMEOUT_MS = 20000;

export function SystemDiscoverEditor({ nickname, collectors, dfMounts, netInterfaces, netExclude, onChange }: Props) {
  const [status, setStatus] = useState<Status>('idle');
  const [describe, setDescribe] = useState<SystemDescribe | null>(null);
  const [reportedAt, setReportedAt] = useState<number | null>(null);
  const pollTimer = useRef<number | null>(null);
  const deadline = useRef(0);
  const requestId = useRef('');
  const alive = useRef(true);

  useEffect(
    () => () => {
      alive.current = false;
      if (pollTimer.current) window.clearTimeout(pollTimer.current);
    },
    [],
  );

  const poll = async () => {
    try {
      const res = await fetchMappingDiscovery(nickname);
      if (!alive.current) return;
      if (res.found && res.requestId === requestId.current && res.describe) {
        setDescribe(res.describe);
        setReportedAt(res.reportedAt ?? Date.now());
        setStatus('idle');
        return;
      }
    } catch {
      // A transient GET failure shouldn't abandon the wait - the next tick
      // tries again, same as the deadline check below.
    }
    if (!alive.current) return;
    if (Date.now() > deadline.current) {
      setStatus('timeout');
      return;
    }
    pollTimer.current = window.setTimeout(() => void poll(), POLL_MS);
  };

  const discover = async () => {
    setStatus('waiting');
    try {
      const { requestId: id } = await requestMappingDiscovery(nickname);
      if (!alive.current) return;
      requestId.current = id;
      deadline.current = Date.now() + TIMEOUT_MS;
      pollTimer.current = window.setTimeout(() => void poll(), POLL_MS);
    } catch {
      if (alive.current) setStatus('error');
    }
  };

  const toggleCollector = (id: string) => onChange({ collectors: collectors.includes(id) ? collectors.filter((c) => c !== id) : [...collectors, id] });

  // Both pickers below collapse to an explicit allow-list the first time
  // the operator touches them (clearing any prior netExclude customization
  // for interfaces) - simpler to reason about than trying to keep a
  // checkbox grid in sync with two different filter mechanisms at once.
  // "every discovered item still checked" clears the allow-list back to
  // "no filter" rather than leaving a redundant exhaustive one.
  const effectiveMounts = dfMounts.length > 0 ? dfMounts : (describe?.mounts ?? []);
  const toggleMount = (m: string) => {
    const next = new Set(effectiveMounts);
    if (next.has(m)) next.delete(m);
    else next.add(m);
    const arr = [...next];
    onChange({ dfMounts: arr.length === (describe?.mounts.length ?? 0) ? [] : arr });
  };

  const virtualSet = new Set(describe?.virtualInterfaces ?? []);
  const effectiveInterfaces = (() => {
    if (netInterfaces.length > 0) return netInterfaces;
    const all = describe?.interfaces ?? [];
    const excluded = new Set(virtualSet);
    for (const i of all) if (netExclude.some((p) => i.startsWith(p))) excluded.add(i);
    return all.filter((i) => !excluded.has(i));
  })();
  const toggleInterface = (i: string) => {
    const next = new Set(effectiveInterfaces);
    if (next.has(i)) next.delete(i);
    else next.add(i);
    onChange({ netInterfaces: [...next], netExclude: [] });
  };

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-black/10 dark:border-white/10 p-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium m-0">Live discovery</p>
          <p className="text-xs text-black/50 dark:text-white/50 m-0">
            Ask the agent running this mapping what it actually sees, then pick what to collect from the real list.
          </p>
        </div>
        <Button variant="secondary" onClick={() => void discover()} disabled={status === 'waiting'}>
          <RefreshCw size={14} className={status === 'waiting' ? 'animate-spin' : undefined} />
          {describe ? 'Refresh' : 'Discover'}
        </Button>
      </div>

      {status === 'waiting' && (
        <p className="text-xs text-black/50 dark:text-white/50 m-0">
          Waiting for the agent to answer (up to {TIMEOUT_MS / 1000}s)…
        </p>
      )}
      {status === 'timeout' && (
        <p className="text-xs text-amber-600 dark:text-amber-400 m-0">
          No agent answered in time. Is <code className="font-mono">zagentc daemon --import</code> running for this mapping, and is it reachable?
        </p>
      )}
      {status === 'error' && <p className="text-xs text-red-500 m-0">Couldn't start discovery - try again.</p>}

      {describe && (
        <div className="flex flex-col gap-3">
          {reportedAt && (
            <p className="text-[11px] text-black/40 dark:text-white/40 m-0">
              From {describe.host || 'the agent'}, {new Date(reportedAt).toLocaleTimeString()}
            </p>
          )}

          <div>
            <p className="text-xs font-medium text-black/60 dark:text-white/60 m-0 mb-1">Collectors</p>
            <div className="flex flex-col gap-1.5">
              {SYSTEM_COLLECTORS.map((c) => {
                const count = describe.collectors[c.id];
                const available = count !== undefined && count > 0;
                return (
                  <label key={c.id} className={`flex items-center gap-2 text-sm ${available ? 'cursor-pointer' : 'opacity-40'}`}>
                    <input
                      type="checkbox"
                      checked={collectors.includes(c.id)}
                      disabled={!available}
                      onChange={() => toggleCollector(c.id)}
                      aria-label={c.label}
                    />
                    <span>
                      {c.label}
                      {available ? (
                        <span className="text-black/40 dark:text-white/40"> ({count} series{c.id === 'cpu' && describe.cpuCores ? `, ${describe.cpuCores} cores` : ''})</span>
                      ) : (
                        <span className="text-black/40 dark:text-white/40"> (not available on this host)</span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {describe.mounts.length > 0 && (
            <div>
              <p className="text-xs font-medium text-black/60 dark:text-white/60 m-0 mb-1">Disk mounts</p>
              <div className="flex flex-wrap gap-1.5">
                {describe.mounts.map((m) => {
                  const checked = effectiveMounts.includes(m);
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => toggleMount(m)}
                      className={`rounded-full px-2.5 py-1 text-[12px] font-mono border ${
                        checked
                          ? 'border-blue-500 bg-blue-500/10 text-blue-700 dark:text-blue-300'
                          : 'border-black/10 dark:border-white/10 text-black/40 dark:text-white/40 line-through'
                      }`}
                    >
                      {m}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {describe.interfaces.length > 0 && (
            <div>
              <p className="text-xs font-medium text-black/60 dark:text-white/60 m-0 mb-1">Network interfaces</p>
              <div className="flex flex-wrap gap-1.5">
                {describe.interfaces.map((i) => {
                  const checked = effectiveInterfaces.includes(i);
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => toggleInterface(i)}
                      className={`rounded-full px-2.5 py-1 text-[12px] font-mono border ${
                        checked
                          ? 'border-blue-500 bg-blue-500/10 text-blue-700 dark:text-blue-300'
                          : 'border-black/10 dark:border-white/10 text-black/40 dark:text-white/40 line-through'
                      }`}
                      title={virtualSet.has(i) ? 'A container/VM interface - hidden by default' : undefined}
                    >
                      {i}
                      {virtualSet.has(i) && <span className="ml-1 text-black/30 dark:text-white/30">(virtual)</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {describe.sensors.length > 0 && (
            <p className="text-xs text-black/50 dark:text-white/50 m-0">
              {describe.sensors.length} temperature sensor(s) found: {describe.sensors.join(', ')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
